import { z } from 'zod';

// ============================================
// ECA 条件定义
// ============================================

/**
 * 技能条件 (ECA - Condition)
 * 用于判断效果是否生效的前提条件
 */
export const SkillConditionSchema = z.object({
  type: z.enum(['hp_below', 'hp_above', 'has_status', 'chance', 'turn_count']),
  /** number: hp_below(0.3=30%), hp_above, chance(0.5=50%), turn_count(5)
   *  string: has_status('poison', 'burn') */
  value: z.union([z.number(), z.string()]),
});
export type SkillCondition = z.infer<typeof SkillConditionSchema>;

/** 效果目标选择器 */
const EffectTargetEnum = z.enum(['self', 'target', 'all_enemies', 'all_allies', 'random_enemy', 'lowest_hp_ally']);

// ============================================
// ECA 效果注册表
// ============================================

const EFFECT_COMMON_FIELDS = {
  target: EffectTargetEnum.default('target'),
  chance: z.number().min(0).max(1).optional(),
  condition: SkillConditionSchema.optional(),
} as const;

const ATTRIBUTE_SCALING_SCHEMA = z.number().min(0).max(5);

function createScalingEffectSchema(type: 'damage' | 'heal') {
  return z.object({
    type: z.literal(type),
    ...EFFECT_COMMON_FIELDS,
    value: z.number(),
    /** 主属性缩放系数（显式填写，如 1 / 1.5 / 2） */
    attributeScaling: ATTRIBUTE_SCALING_SCHEMA,
  });
}

function createApplyStatusEffectSchema() {
  return z.object({
    type: z.literal('apply_status'),
    ...EFFECT_COMMON_FIELDS,
    value: z.number().optional(),
    statusId: z.string(),
    duration: z.number().min(0).optional(),
  });
}

function createRemoveStatusEffectSchema() {
  return z.object({
    type: z.literal('remove_status'),
    ...EFFECT_COMMON_FIELDS,
    statusId: z.string(),
  });
}

function createDiscriminatedUnionFromRegistry<T extends Record<string, z.ZodObject<any>>>(registry: T) {
  const options = Object.values(registry) as [z.ZodObject<any>, ...z.ZodObject<any>[]];
  return z.discriminatedUnion('type', options as any);
}

/**
 * 连锁效果注册表
 * 注意：连锁层不包含 onHit，防止无限递归
 */
export const CHAINED_EFFECT_SCHEMA_REGISTRY = {
  damage: createScalingEffectSchema('damage'),
  heal: createScalingEffectSchema('heal'),
  apply_status: createApplyStatusEffectSchema(),
  remove_status: createRemoveStatusEffectSchema(),
} as const;

export const ChainedSkillEffectSchema = createDiscriminatedUnionFromRegistry(CHAINED_EFFECT_SCHEMA_REGISTRY);
export type ChainedSkillEffect = z.infer<typeof ChainedSkillEffectSchema>;

function withOnHit<T extends z.ZodObject<any>>(schema: T) {
  return schema.extend({
    /** 主效果命中后触发的追加效果（最多一层，不可再嵌套） */
    onHit: z.array(ChainedSkillEffectSchema).optional(),
  });
}

/**
 * 主效果注册表
 * 强约束：damage/heal 必须显式提供 value 与 attributeScaling
 */
export const SKILL_EFFECT_SCHEMA_REGISTRY = {
  damage: withOnHit(createScalingEffectSchema('damage')),
  heal: withOnHit(createScalingEffectSchema('heal')),
  apply_status: withOnHit(createApplyStatusEffectSchema()),
  remove_status: withOnHit(createRemoveStatusEffectSchema()),
} as const;

export const SkillEffectSchema = createDiscriminatedUnionFromRegistry(SKILL_EFFECT_SCHEMA_REGISTRY);
export type SkillEffect = z.infer<typeof SkillEffectSchema>;
